import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { PlusIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, Button, SearchInput, Select,
  Table, TableHead, TableBody, TableRow, TableHeader, TableCell,
  StatusBadge, Pagination, EmptyState, PageLoader
} from '../../components/ui';
import { invoiceService } from '../../services';

export default function InvoiceList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  
  useEffect(() => {
    fetchInvoices();
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters]);
  
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      const response = await invoiceService.getInvoices(params);
      setInvoices(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const data = await invoiceService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  const handleSearch = useCallback((value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);
  
  const handleStatusFilter = (e) => {
    setFilters(prev => ({ ...prev, status: e.target.value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };
  
  const statusOptions = [
    { value: '', label: t('common.all') },
    { value: 'draft', label: t('invoices.statuses.draft') },
    { value: 'sent', label: t('invoices.statuses.sent') },
    { value: 'paid', label: t('invoices.statuses.paid') },
    { value: 'partial', label: t('invoices.statuses.partial') },
    { value: 'overdue', label: t('invoices.statuses.overdue') }
  ];
  
  return (
    <>
      <Helmet>
        <title>{t('invoices.title')} - ProCTM</title>
      </Helmet>
      
      <PageHeader
        title={t('invoices.title')}
        actions={
          <Button icon={PlusIcon} onClick={() => navigate('/invoices/new')}>
            {t('invoices.newInvoice')}
          </Button>
        }
      />
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardBody className="p-4">
              <p className="text-sm text-gray-500">{t('dashboard.totalRevenue')}</p>
              <p className="text-xl font-semibold text-success">${stats.totalRevenue?.toLocaleString() || 0}</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4">
              <p className="text-sm text-gray-500">{t('dashboard.outstanding')}</p>
              <p className="text-xl font-semibold text-accent">${stats.totalOutstanding?.toLocaleString() || 0}</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4">
              <p className="text-sm text-gray-500">{t('invoices.statuses.paid')}</p>
              <p className="text-xl font-semibold text-primary">{stats.paid?.count || 0}</p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="p-4">
              <p className="text-sm text-gray-500">{t('invoices.statuses.overdue')}</p>
              <p className="text-xl font-semibold text-danger">{stats.overdue?.count || 0}</p>
            </CardBody>
          </Card>
        </div>
      )}
      
      <Card>
        <CardBody>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <SearchInput
              value={filters.search}
              onChange={handleSearch}
              placeholder={t('invoices.invoiceNumber')}
              className="sm:w-64"
            />
            <Select
              options={statusOptions}
              value={filters.status}
              onChange={handleStatusFilter}
              className="sm:w-40"
            />
          </div>
          
          {loading ? (
            <PageLoader />
          ) : invoices.length === 0 ? (
            <EmptyState
              icon={DocumentTextIcon}
              title={t('invoices.noInvoices')}
              description={t('invoices.noInvoicesDesc')}
              action={() => navigate('/invoices/new')}
              actionLabel={t('invoices.newInvoice')}
            />
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>{t('invoices.invoiceNumber')}</TableHeader>
                    <TableHeader>{t('projects.client')}</TableHeader>
                    <TableHeader>{t('invoices.total')}</TableHeader>
                    <TableHeader>{t('invoices.amountDue')}</TableHeader>
                    <TableHeader>{t('invoices.dueDate')}</TableHeader>
                    <TableHeader>{t('common.status')}</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow
                      key={invoice._id}
                      onClick={() => navigate(`/invoices/${invoice._id}`)}
                      className="cursor-pointer"
                    >
                      <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                      <TableCell>{invoice.client?.name || '-'}</TableCell>
                      <TableCell>${invoice.total?.toLocaleString()}</TableCell>
                      <TableCell className={invoice.amountDue > 0 ? 'text-danger' : ''}>
                        ${invoice.amountDue?.toLocaleString()}
                      </TableCell>
                      <TableCell>{dayjs(invoice.dueDate).format('MMM D, YYYY')}</TableCell>
                      <TableCell><StatusBadge status={invoice.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <Pagination
                page={pagination.page}
                limit={pagination.limit}
                total={pagination.total}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </CardBody>
      </Card>
    </>
  );
}
